import { useToasts } from '@mnd-frontend/ui';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import VideoUploader from '../../../../components/MediaUploaders/VideoUploader';
import { useVideoQueryQuery } from '../../../../generated/hooks';
import useVideoUpload from '../EditMaterials/VideoUploader/useVideoUpload';
import { UploadQueue } from './utils/UploadQueue';

const StyledDiv = styled.div`
  display: flex;
  flex-direction: column;
  > *:first-child {
    flex: 1;
  }
  > *:nth-child(2) {
    margin-top: 0.5rem;
  }
`;

const InnerVideoUploaderCell = ({
  setPendingFiles,
  refetch,
  hidden = false,
  pendingVideoId,
}: {
  setPendingFiles: React.Dispatch<React.SetStateAction<number>>;
  refetch: () => void;
  hidden?: boolean;
  pendingVideoId?: string;
}) => {
  const { showFailureToast } = useToasts();
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const uploadQueueRef = useRef<UploadQueue | null>(null);
  const [t] = useTranslation();
  const [processed, setProcessed] = useState<boolean | undefined>();

  const [uploadVideo, { data }] = useVideoUpload({
    onError: (error?: string) => showFailureToast(error ?? t('common_general_error')),
    onCompleted: response => {
      const error = response.videoSyncScreen9Upload?.error;
      const responseProcessed = response.videoSyncScreen9Upload?.video?.resourceVideo?.processed;
      if (error) {
        showFailureToast(error.message || t('common_general_error'));
      } else if (responseProcessed !== undefined) {
        setProcessed(responseProcessed);
      }
    },
  });
  const createdVideo = data?.videoSyncScreen9Upload?.video;

  const getUploadQueue = () => {
    if (uploadQueueRef.current) return uploadQueueRef.current;

    uploadQueueRef.current = new UploadQueue();
    return uploadQueueRef.current;
  };
  const { startPolling, stopPolling } = useVideoQueryQuery({
    variables: {
      id: createdVideo?.id || pendingVideoId!,
    },
    skip: processed || !(createdVideo?.id || pendingVideoId),
    notifyOnNetworkStatusChange: true,
    onCompleted: pollData => {
      if (
        pollData?.material?.__typename === 'Video' &&
        pollData.material.resourceVideo?.processed !== undefined
      ) {
        setProcessed(pollData.material.resourceVideo.processed);
      }
    },
    onError: () => {
      showFailureToast(t('common_general_error'));
    },
  });

  useEffect(() => {
    if (processed === false || pendingVideoId) {
      startPolling(2000);
      return () => {
        stopPolling();
      };
    }
    return;
  }, [processed, startPolling, stopPolling, pendingVideoId]);

  useEffect(() => {
    getUploadQueue().updateFunctionPointers({
      upload: uploadVideo,
      onStarted: () => setUploadingVideo(true),
      onCompleted: () => setUploadingVideo(false),
      onFileUploaded: refetch,
      onCountChanged: count => {
        setPendingFiles(count);
      },
    });
  });

  useEffect(() => () => getUploadQueue().destroy(), [setPendingFiles]);

  return (
    <StyledDiv style={hidden ? { display: 'none' } : undefined}>
      <VideoUploader
        loading={uploadingVideo || processed === false}
        action={(file: Blob) => {
          setProcessed(undefined);
          getUploadQueue().queue([file]);
        }}
      />
    </StyledDiv>
  );
};

export const VideoUploaderCell = React.memo(InnerVideoUploaderCell);
